/** Kalenderjahr performance table — TableData shape (same as other factsheet Configure Table dialogs). */

export type KalenderjahrTableData = {
  labels: string[];
  values: string[][];
  columns?: Array<{ type?: 'text' | 'avatar' | 'link' }>;
};

/** Legacy flat parallel arrays (pre–TableData). */
export type KalenderjahrLegacy = {
  years?: string[];
  values?: string[];
};

const COLS = 8; // label + 7 year cells
const CHUNK = 7;

const textCols = () =>
  Array.from({ length: COLS }, () => ({ type: 'text' as const }));

/** Default matching original factsheet groups of 7 (2005–2025). */
export const DEFAULT_KALENDERJAHR: KalenderjahrTableData = {
  labels: ['', '1', '2', '3', '4', '5', '6', '7'],
  values: [
    ['Jahr', '2005', '2006', '2007', '2008', '2009', '2010', '2011'],
    ['Rendite', '33,89 %', '22,47 %', '9,50 %', '-36,42 %', '47,51 %', '19,36 %', '-0,59 %'],
    ['Jahr', '2012', '2013', '2014', '2015', '2016', '2017', '2018'],
    ['Rendite', '15,18 %', '16,24 %', '0,17 %', '15,56 %', '9,56 %', '20,14 %', '-12,70 %'],
    ['Jahr', '2019', '2020', '2021', '2022', '2023', '2024', '2025'],
    ['Rendite', '18,82 %', '4,27 %', '18,64 %', '-14,24 %', '13,34 %', '5,04 %', '12,60 %'],
  ],
  columns: textCols(),
};

function cellText(v: unknown): string {
  if (v == null) return '';
  if (typeof v === 'string' || typeof v === 'number') return String(v);
  if (typeof v === 'object' && v !== null && 'text' in v) return String((v as any).text ?? '');
  return String(v);
}

/** Normalize saved data (TableData or legacy years/values) → TableData for dialog + render. */
export function normalizeKalenderjahrData(raw: unknown): KalenderjahrTableData {
  if (!raw || typeof raw !== 'object') return DEFAULT_KALENDERJAHR;

  const data = raw as KalenderjahrTableData & KalenderjahrLegacy;

  if (Array.isArray(data.labels) && Array.isArray(data.values) && Array.isArray(data.values[0])) {
    const values = data.values.map((row) => {
      const cells = (row || []).map(cellText);
      while (cells.length < COLS) cells.push('');
      return cells.slice(0, Math.max(COLS, cells.length));
    });
    const colCount = Math.max(COLS, ...values.map((r) => r.length), data.labels.length);
    const labels = [...data.labels.map(String)];
    while (labels.length < colCount) labels.push(String(labels.length));
    return {
      labels: labels.slice(0, colCount),
      values: values.map((r) => {
        const next = [...r];
        while (next.length < colCount) next.push('');
        return next.slice(0, colCount);
      }),
      columns: data.columns?.length
        ? data.columns
        : Array.from({ length: colCount }, () => ({ type: 'text' as const })),
    };
  }

  // Legacy { years, values }
  const years = Array.isArray(data.years) ? data.years.map(String) : [];
  const vals = Array.isArray((data as KalenderjahrLegacy).values)
    ? ((data as KalenderjahrLegacy).values as string[]).map(String)
    : [];
  if (!years.length) return DEFAULT_KALENDERJAHR;

  const rows: string[][] = [];
  for (let i = 0; i < years.length; i += CHUNK) {
    const yChunk = years.slice(i, i + CHUNK);
    const vChunk = vals.slice(i, i + CHUNK);
    const yearRow = ['Jahr', ...yChunk.map((y) => (y.length <= 2 ? `20${y}` : y))];
    const renditeRow = ['Rendite', ...vChunk];
    while (yearRow.length < COLS) yearRow.push('');
    while (renditeRow.length < COLS) renditeRow.push('');
    rows.push(yearRow.slice(0, COLS), renditeRow.slice(0, COLS));
  }

  return {
    labels: ['', '1', '2', '3', '4', '5', '6', '7'],
    values: rows,
    columns: textCols(),
  };
}

export function kalenderjahrHasData(raw: unknown): boolean {
  const data = normalizeKalenderjahrData(raw);
  return data.values.some((row) => row.some((c, i) => i > 0 && String(c).trim() !== ''));
}
